import React from "react";
import { useObjectUrl } from "../../../hooks";
import { useBackgroundNav } from "../../../contexts/backgroundNav";
import Backdrop from "../../../components/Backdrop";
import "./Image.sass";
import { defaultCache, Props } from "./types";

const CURSOR_KEY = "lumina-image-cursor";

function getCursor(): number {
  try {
    const v = localStorage.getItem(CURSOR_KEY);
    return v ? parseInt(v, 10) : 0;
  } catch {
    return 0;
  }
}

function saveCursor(cursor: number) {
  try {
    localStorage.setItem(CURSOR_KEY, String(cursor));
  } catch {}
}

const Image: React.FC<Props> = ({ cache = defaultCache }) => {
  const [, forceUpdate] = React.useReducer((x) => x + 1, 0);
  const { setNav } = useBackgroundNav();

  const count = cache.length;
  const cursor = count > 0 ? getCursor() % count : 0;
  const url = useObjectUrl(cache[cursor] ?? null);

  // loadedUrl is only set AFTER the browser has decoded the image
  // (via <img onLoad>).  Comparing it against url in render gives an
  // instantaneous answer — no one-frame gap.
  const [loadedUrl, setLoadedUrl] = React.useState<string | null>(null);
  const isReady = url !== null && loadedUrl === url;

  // ── navigation ────────────────────────────────────────────────────
  const goNext = React.useCallback(() => {
    if (count <= 1) return;
    const next = (getCursor() + 1) % count;
    saveCursor(next);
    forceUpdate();
  }, [count]);

  const goPrev = React.useCallback(() => {
    if (count <= 1) return;
    const cur = getCursor();
    const prev = (cur - 1 + count) % count;
    saveCursor(prev);
    forceUpdate();
  }, [count]);

  React.useEffect(() => {
    if (count > 1) {
      setNav({ goNext, goPrev });
      return () => setNav({ goNext: null, goPrev: null });
    }
  }, [goNext, goPrev, setNav, count]);

  // ── render ────────────────────────────────────────────────────────
  if (!url) return <div className="Image default fullscreen" />;

  return (
    <div className="Image fullscreen">
      <Backdrop className="image fullscreen" ready={isReady}>
        <img
          className="Image__img"
          src={url}
          alt=""
          onLoad={() => setLoadedUrl(url)}
          onError={() => setLoadedUrl(url)}
        />
      </Backdrop>

      {!isReady && (
        <div className="Image__loading">
          <div className="Image__spinner" />
          <span>Loading…</span>
        </div>
      )}
    </div>
  );
};

export default Image;
